import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { fetchContent, adminUpdateContent, adminUploadImage, imgUrl } from "@/lib/api";
import { useCategories } from "@/context/CategoriesContext";

const inputCls = "w-full border border-border px-3 py-2 font-mono text-sm focus:outline-none focus:ring-1 focus:ring-foreground";

const ContentAdmin = () => {
  const { categories } = useCategories();
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sgUploading, setSgUploading] = useState(false);
  const [sgCat, setSgCat] = useState(""); // "" = global default
  useEffect(() => { fetchContent().then(setC); }, []);
  if (!c) return <div className="p-8 font-mono text-xs uppercase tracking-widest text-muted-foreground">…</div>;

  const set = (k, v) => setC((x) => ({ ...x, [k]: v }));
  const setAnn = (k, v) => setC((x) => ({ ...x, announcement: { ...x.announcement, [k]: v } }));
  const setCS = (k, v) => setC((x) => ({ ...x, coming_soon: { ...(x.coming_soon || {}), [k]: v } }));
  const setLegal = (k, v) => setC((x) => ({ ...x, legal: { ...(x.legal || {}), [k]: v } }));
  const setFaq = (i, k, v) => setC((x) => { const f = [...(x.faq || [])]; f[i] = { ...f[i], [k]: v }; return { ...x, faq: f }; });

  const cs = c.coming_soon || {};
  const legal = c.legal || {};
  // Size guide: global (c.size_guide) or per-category (c.size_guides[slug])
  const curSG = sgCat ? ((c.size_guides || {})[sgCat] || {}) : (c.size_guide || {});
  const patchSG = (patch) => setC((x) => {
    if (sgCat) {
      const map = { ...(x.size_guides || {}) };
      map[sgCat] = { ...(map[sgCat] || {}), ...patch };
      return { ...x, size_guides: map };
    }
    return { ...x, size_guide: { ...(x.size_guide || {}), ...patch } };
  });
  const setSGField = (k, v) => patchSG({ [k]: v });
  const openPreview = () => window.open(`${window.location.origin}/?preview=1`, "_blank", "noopener");

  const onUploadSG = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    setSgUploading(true);
    try { const { url } = await adminUploadImage(file); setSGField("image", url); toast.success("Poză încărcată"); }
    catch { toast.error("Upload eșuat"); }
    setSgUploading(false); ev.target.value = "";
  };

  const save = async () => {
    setSaving(true);
    try { const upd = await adminUpdateContent(c); setC(upd); toast.success("Salvat"); } catch { toast.error("Eroare"); }
    setSaving(false);
  };

  return (
    <div className="p-5 sm:p-8" data-testid="content-admin">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-xl uc font-semibold">Conținut site</h2>
        <button data-testid="content-save" onClick={save} disabled={saving} className="bg-foreground px-6 py-3 font-mono text-[11px] uppercase tracking-widest text-white hover:opacity-85 disabled:opacity-50">{saving ? "..." : "Salvează"}</button>
      </div>

      <div className="mb-8 border-b border-border pb-8" data-testid="sales-settings">
        <div className="mb-3 font-mono text-[11px] uppercase tracking-widest">Reduceri (SALE!)</div>
        <label className="flex items-center gap-2">
          <input data-testid="sales-enabled" type="checkbox" checked={!!c.sales_enabled} onChange={(e) => set("sales_enabled", e.target.checked)} />
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Reduceri active pe site (prețuri vechi tăiate + insigne SALE!)</span>
        </label>
        <p className="mt-2 font-mono text-[10px] uppercase leading-relaxed tracking-widest text-muted-foreground">
          OFF = toate reducerile dispar de pe site, dar prețurile vechi rămân salvate pe produse. ON = apar instant înapoi.
        </p>
      </div>

      <div className="mb-8 border-b border-border pb-8" data-testid="coming-soon-settings">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Mod „Coming Soon"</span>
          <button
            type="button"
            data-testid="coming-soon-preview"
            onClick={openPreview}
            className="border border-border px-4 py-2 font-mono text-[10px] uppercase tracking-widest hover:border-foreground"
          >
            Previzualizează site-ul →
          </button>
        </div>
        <label className="mb-3 flex items-center gap-2">
          <input data-testid="coming-soon-enabled" type="checkbox" checked={!!cs.enabled} onChange={(e) => setCS("enabled", e.target.checked)} />
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Afișează „Coming Soon" vizitatorilor (tu vezi în continuare tot din admin + previzualizare)
          </span>
        </label>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <input className={inputCls} value={cs.ro || ""} onChange={(e) => setCS("ro", e.target.value)} placeholder="Titlu RO (ex: ÎN CURÂND)" />
          <input className={inputCls} value={cs.en || ""} onChange={(e) => setCS("en", e.target.value)} placeholder="Title EN (ex: COMING SOON)" />
          <input className={inputCls} value={cs.sub_ro || ""} onChange={(e) => setCS("sub_ro", e.target.value)} placeholder="Subtitlu RO" />
          <input className={inputCls} value={cs.sub_en || ""} onChange={(e) => setCS("sub_en", e.target.value)} placeholder="Subtitle EN" />
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Countdown până la lansare</span>
          <input type="datetime-local" data-testid="coming-soon-countdown" className={inputCls + " max-w-[240px]"} value={cs.countdown || ""} onChange={(e) => setCS("countdown", e.target.value)} />
          {cs.countdown ? <button type="button" onClick={() => setCS("countdown", "")} className="font-mono text-[10px] uppercase tracking-widest text-destructive">șterge</button> : null}
        </div>
      </div>

      <div className="mb-8 border-b border-border pb-8">
        <label className="mb-3 flex items-center gap-2"><input data-testid="ann-enabled" type="checkbox" checked={!!c.announcement?.enabled} onChange={(e) => setAnn("enabled", e.target.checked)} /><span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Bară anunț activă</span></label>
        <input className={inputCls + " mb-2"} value={c.announcement?.ro || ""} onChange={(e) => setAnn("ro", e.target.value)} placeholder="Text RO" />
        <input className={inputCls} value={c.announcement?.en || ""} onChange={(e) => setAnn("en", e.target.value)} placeholder="Text EN" />
      </div>

      <div className="mb-8 grid grid-cols-1 gap-4 border-b border-border pb-8 sm:grid-cols-2">
        <div><div className="mb-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Despre (RO)</div><textarea className={inputCls} rows={4} value={c.about_ro || ""} onChange={(e) => set("about_ro", e.target.value)} /></div>
        <div><div className="mb-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Despre (EN)</div><textarea className={inputCls} rows={4} value={c.about_en || ""} onChange={(e) => set("about_en", e.target.value)} /></div>
      </div>

      <div>
        <div className="mb-3 flex items-center justify-between"><span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Întrebări (FAQ)</span>
          <button onClick={() => set("faq", [...(c.faq || []), { q_ro: "", q_en: "", a_ro: "", a_en: "" }])} className="font-mono text-[10px] uppercase tracking-widest hover:opacity-60">+ întrebare</button></div>
        <div className="space-y-4">
          {(c.faq || []).map((f, i) => (
            <div key={i} className="border border-border p-3">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <input className={inputCls} value={f.q_ro} onChange={(e) => setFaq(i, "q_ro", e.target.value)} placeholder="Întrebare RO" />
                <input className={inputCls} value={f.q_en} onChange={(e) => setFaq(i, "q_en", e.target.value)} placeholder="Question EN" />
                <input className={inputCls} value={f.a_ro} onChange={(e) => setFaq(i, "a_ro", e.target.value)} placeholder="Răspuns RO" />
                <input className={inputCls} value={f.a_en} onChange={(e) => setFaq(i, "a_en", e.target.value)} placeholder="Answer EN" />
              </div>
              <button onClick={() => set("faq", c.faq.filter((_, j) => j !== i))} className="mt-2 font-mono text-[10px] uppercase tracking-widest text-destructive">Șterge</button>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8 border-t border-border pt-8" data-testid="size-guide-settings">
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Ghid de mărimi</span>
          <select data-testid="size-guide-category" value={sgCat} onChange={(e) => setSgCat(e.target.value)} className={inputCls + " max-w-[240px]"}>
            <option value="">Global (implicit)</option>
            {categories.map((cat) => <option key={cat.slug} value={cat.slug}>{cat.ro}</option>)}
          </select>
          <span className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">{sgCat ? "editezi ghidul pentru această categorie" : "folosit când o categorie nu are ghid propriu"}</span>
        </div>
        <div className="flex flex-wrap items-start gap-4">
          {curSG.image ? (
            <div className="relative h-40 w-40 border border-border">
              <div className="flex h-full w-full items-center justify-center overflow-hidden bg-white p-1"><img src={imgUrl(curSG.image)} alt="" className="max-h-full max-w-full object-contain" /></div>
              <button onClick={() => setSGField("image", "")} className="absolute bottom-0 right-0 bg-white/90 px-1 font-mono text-[9px] uppercase text-destructive">șterge</button>
            </div>
          ) : null}
          <label className="flex h-40 w-40 cursor-pointer flex-col items-center justify-center border border-dashed border-border text-center font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:bg-secondary">
            {sgUploading ? "..." : "+ Poză măsurători"}
            <input data-testid="size-guide-upload" type="file" accept="image/*" onChange={onUploadSG} className="hidden" />
          </label>
        </div>
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
          <textarea className={inputCls} rows={2} value={curSG.desc_ro || ""} onChange={(e) => setSGField("desc_ro", e.target.value)} placeholder="Text sub titlu RO (ex: Măsurători în centimetri...)" data-testid="size-guide-desc-ro" />
          <textarea className={inputCls} rows={2} value={curSG.desc_en || ""} onChange={(e) => setSGField("desc_en", e.target.value)} placeholder="Subtitle text EN" data-testid="size-guide-desc-en" />
        </div>

        {(() => {
          const cols = (curSG.cols && curSG.cols.length) ? curSG.cols : ["MĂRIME", "PIEPT", "LUNGIME", "UMĂR"];
          const rows = curSG.rows || [];
          const setCol = (i, v) => { const a = [...cols]; a[i] = v; patchSG({ cols: a }); };
          const addCol = () => patchSG({ cols: [...cols, "COL"], rows: rows.map((r) => [...r, ""]) });
          const delCol = (i) => patchSG({ cols: cols.filter((_, j) => j !== i), rows: rows.map((r) => r.filter((_, j) => j !== i)) });
          const addRow = () => patchSG({ cols, rows: [...rows, cols.map(() => "")] });
          const delRow = (r) => patchSG({ rows: rows.filter((_, j) => j !== r) });
          const setCell = (r, c, v) => patchSG({ rows: rows.map((row, ri) => (ri === r ? row.map((cell, ci) => (ci === c ? v : cell)) : row)) });
          return (
            <div className="mt-4" data-testid="size-guide-table-editor">
              <div className="mb-2 flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Tabel de mărimi (lasă gol = tabelul implicit)</span>
                <div className="flex gap-3">
                  <button type="button" onClick={addCol} className="font-mono text-[10px] uppercase tracking-widest hover:opacity-60">+ coloană</button>
                  <button type="button" onClick={addRow} data-testid="size-guide-add-row" className="font-mono text-[10px] uppercase tracking-widest hover:opacity-60">+ rând</button>
                </div>
              </div>
              <div className="overflow-x-auto border border-border">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-secondary">
                      {cols.map((h, i) => (
                        <th key={i} className="border border-border p-1">
                          <div className="flex items-center gap-1">
                            <input className="w-full bg-transparent px-1 py-1 font-mono text-[10px] uppercase focus:outline-none" value={h} onChange={(e) => setCol(i, e.target.value)} />
                            {cols.length > 1 && <button type="button" onClick={() => delCol(i)} className="font-mono text-[10px] text-destructive">×</button>}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr><td colSpan={cols.length} className="p-3 text-center font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Niciun rând — se folosește tabelul implicit</td></tr>
                    ) : rows.map((row, ri) => (
                      <tr key={ri}>
                        {cols.map((_, ci) => (
                          <td key={ci} className="border border-border p-1">
                            <input className="w-full bg-transparent px-1 py-1 font-mono text-xs focus:outline-none" value={row[ci] ?? ""} onChange={(e) => setCell(ri, ci, e.target.value)} data-testid={`sg-cell-${ri}-${ci}`} />
                          </td>
                        ))}
                        <td className="border border-border p-1 text-center"><button type="button" onClick={() => delRow(ri)} className="font-mono text-xs text-destructive">×</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-2 font-mono text-[9px] uppercase tracking-widest text-muted-foreground">Notă opțională sub tabel:</p>
              <div className="mt-1 grid grid-cols-1 gap-2 sm:grid-cols-2">
                <textarea className={inputCls} rows={2} value={curSG.note_ro || ""} onChange={(e) => setSGField("note_ro", e.target.value)} placeholder="Notă RO" data-testid="size-guide-note-ro" />
                <textarea className={inputCls} rows={2} value={curSG.note_en || ""} onChange={(e) => setSGField("note_en", e.target.value)} placeholder="Note EN" data-testid="size-guide-note-en" />
              </div>
            </div>
          );
        })()}
      </div>

      <div className="mt-8 border-t border-border pt-8" data-testid="legal-settings">
        <div className="mb-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Pagini legale (lasă gol = text implicit)</div>
        <div className="space-y-4">
          {[
            ["termeni", "Termeni & Condiții"],
            ["retur", "Politica de retur"],
            ["gdpr", "Confidențialitate (GDPR)"],
          ].map(([key, lbl]) => (
            <div key={key} className="border border-border p-3">
              <div className="mb-2 font-mono text-[10px] uppercase tracking-widest">{lbl}</div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <textarea className={inputCls} rows={5} value={legal[`${key}_ro`] || ""} onChange={(e) => setLegal(`${key}_ro`, e.target.value)} placeholder="Text RO" data-testid={`legal-${key}-ro`} />
                <textarea className={inputCls} rows={5} value={legal[`${key}_en`] || ""} onChange={(e) => setLegal(`${key}_en`, e.target.value)} placeholder="Text EN" data-testid={`legal-${key}-en`} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ContentAdmin;
